import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { programsAPI, contractsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function ProgramFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    program_name: '', program_number: '', customer_id: '', description: '', status: 'active', email_recipients: '', default_cc_recipients: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const custRes = await contractsAPI.listCustomers({ status: 'active' });
        setCustomers(custRes.data.customers || custRes.data || []);
        if (isEdit) {
          const res = await programsAPI.get(id);
          const p = res.data;
          setForm({
            program_name: p.program_name || '',
            program_number: p.program_number || '',
            customer_id: p.customer_id || '',
            description: p.description || '',
            status: p.status || 'active',
            email_recipients: p.email_recipients || '',
            default_cc_recipients: p.default_cc_recipients || '',
          });
        }
      } catch { toast.error('Failed to load data'); }
    })();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.program_name || !form.program_number || !form.customer_id) {
      toast.error('Program name, number and customer are required');
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await programsAPI.update(id, form);
        toast.success('Program updated');
        navigate(`/programs/${id}`);
      } else {
        const res = await programsAPI.create(form);
        toast.success('Program created');
        navigate(`/programs/${res.data.id}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="program-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="font-heading text-xl font-bold">{isEdit ? 'Edit Program' : 'New Program'}</h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Program Name *</Label>
                <Input className="h-9 text-sm mt-1" value={form.program_name} onChange={e => setForm(p => ({ ...p, program_name: e.target.value }))} data-testid="program-name-input" />
              </div>
              <div>
                <Label className="text-xs">Program Number *</Label>
                <Input className="h-9 text-sm mt-1 font-mono" value={form.program_number} onChange={e => setForm(p => ({ ...p, program_number: e.target.value }))} data-testid="program-number-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Customer *</Label>
              <Select value={form.customer_id} onValueChange={v => setForm(p => ({ ...p, customer_id: v }))}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="customer-select"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.customer_name} ({c.customer_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea className="text-sm mt-1" rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="description-input" />
            </div>
            <div>
              <Label className="text-xs">Email Recipients</Label>
              <Input className="h-9 text-sm mt-1" value={form.email_recipients} onChange={e => setForm(p => ({ ...p, email_recipients: e.target.value }))} placeholder="Comma-separated emails (optional)" data-testid="email-recipients-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Used for PO acknowledgment emails linked to this program. Leave blank to use customer default.</p>
            </div>
            <div>
              <Label className="text-xs">Default CC Recipients</Label>
              <Input className="h-9 text-sm mt-1" value={form.default_cc_recipients} onChange={e => setForm(p => ({ ...p, default_cc_recipients: e.target.value }))} placeholder="Comma-separated emails (optional)" data-testid="default-cc-recipients-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Auto-CC&apos;d on every dispatch email containing items from this program. Standard internal CCs (toolroom, AE) are always added on top.</p>
            </div>
            {isEdit && (
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger className="h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="submit" size="sm" disabled={loading} data-testid="submit-btn">{loading ? 'Saving...' : isEdit ? 'Update' : 'Create Program'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
